import { useDebouncedCallback } from '@react-hookz/web';
import { useEffect } from 'react';
import { ARENA_SELECTOR } from '../../../../const';
import { DEBOUNCE_DELAY } from '../constants';

/**
 * Generic hook to add debounced resize observer and scroll handlers to an element.
 * Used as an internal building block for overlay positioning hooks.
 */
export function useContainerResizeAndScrollHandlers(
  containerEl: HTMLElement | null,
  onUpdate: () => void,
) {
  const handleUpdate = useDebouncedCallback(onUpdate, [onUpdate], DEBOUNCE_DELAY);

  useEffect(() => {
    if (!containerEl) {
      return;
    }

    // in walkthrough builder, containerEl is the iframe body but the arena lives
    // in the parent (builder) document — closest() never finds it. fall back to
    // the parent doc so we still attach the scroll listener to the scrolling shell.
    const arenaElement =
      containerEl.closest(ARENA_SELECTOR) ??
      containerEl.ownerDocument.defaultView?.parent?.document?.querySelector(ARENA_SELECTOR) ??
      null;
    const targetPageContainerElement = containerEl.closest('[data-name="target-page-container"]');

    if (!arenaElement || !targetPageContainerElement) {
      console.debug('Arena or canvas element not found, observers will not be set up');
      return;
    }

    const resizeObserver = new ResizeObserver(handleUpdate);

    // Observe canvas for size changes (independent of window resize)
    resizeObserver.observe(targetPageContainerElement);
    // Listen to scroll events to update tooltip position
    arenaElement.addEventListener('scroll', handleUpdate, { passive: true });

    return () => {
      resizeObserver.unobserve(targetPageContainerElement);
      arenaElement.removeEventListener('scroll', handleUpdate);
    };
  }, [containerEl, handleUpdate]);
}

/**
 * Hook that handles all resize and scroll events for walkthrough overlays.
 * Integrates both global (window) and local (canvas/arena) listeners.
 */
export function useWalkthroughResizeHandlers(params: {
  targetBlockId: string | undefined;
  containerEl: HTMLElement | null;
  onUpdate: () => void;
  isBuilderOrPreview: boolean;
}): void {
  const { targetBlockId, containerEl, onUpdate, isBuilderOrPreview } = params;
  const handleUpdate = useDebouncedCallback(onUpdate, [onUpdate], DEBOUNCE_DELAY);

  useEffect(() => {
    if (!targetBlockId) return;
    const targetWindow = containerEl?.ownerDocument.defaultView ?? window;
    targetWindow.addEventListener('resize', handleUpdate);
    return () => targetWindow.removeEventListener('resize', handleUpdate);
  }, [targetBlockId, handleUpdate, containerEl]);

  useContainerResizeAndScrollHandlers(isBuilderOrPreview ? containerEl : null, onUpdate);
}
